import React, { useCallback, useState } from 'react';
import { RefreshControl, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { LopMonthSummary } from '../../components/LopMonthSummary';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { normalizeApiError } from '../../api/apiError';
import { getEmployeeLeaveBalancesAdmin } from '../../services/leaveBalanceAdminService';
import type { LeaveBalanceEmployeeResult } from '../../types/leaveBalanceAdmin';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import { formatLeaveBalanceValue } from '../../utils/leaveSession';
import { leaveBalanceDisplayColor } from '../../utils/leaveBalance';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'LeaveBalanceEmployee'>;

export function LeaveBalanceEmployeeScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const canUpdate = auth.hasPermission(PERMISSIONS.SETTINGS_UPDATE);
  const [year, setYear] = useState(String(route.params.leaveYear || new Date().getFullYear()));
  const [data, setData] = useState<LeaveBalanceEmployeeResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (refresh = false) => {
    const leaveYear = Number(year);
    if (!Number.isInteger(leaveYear) || leaveYear < 2000 || leaveYear > 2200) {
      setError('Enter a valid leave year.');
      setLoading(false);
      return;
    }
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      setData(await getEmployeeLeaveBalancesAdmin(route.params.employeeId, leaveYear));
      setError(null);
    } catch (caught) {
      setError(normalizeApiError(caught).message || 'Unable to load leave balances.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [route.params.employeeId, year]);

  useFocusEffect(useCallback(() => {
    load().catch(() => undefined);
  }, [load]));

  return (
    <PermissionGuard permission={PERMISSIONS.SETTINGS_VIEW} fallback={<UnauthorizedScreen />}>
      <AppScreen
        scroll
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => load(true)} />}
      >
        <ScreenHeader title="Employee leave balances" onBack={navigation.goBack} />
        <View style={styles.body}>
          <AppInput label="Year" value={year} onChangeText={setYear} keyboardType="number-pad" />
          <AppButton title="View balances" variant="secondary" onPress={() => load()} />
          {loading ? <AppLoader message="Loading leave balances" /> : null}
          {error && !data && !loading ? <ErrorState message={error} onRetry={() => load()} /> : null}
          {data ? (
            <>
              <AppCard style={styles.card}>
                <Text style={[styles.title, { color: palette.textPrimary }]}>
                  {data.employee.displayName}
                </Text>
                <Text style={[styles.meta, { color: palette.textSecondary }]}>
                  {[data.employee.employeeNumber, data.employee.departmentName, data.employee.designationName]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
                {data.lopSummary ? (
                  <LopMonthSummary summary={data.lopSummary} palette={palette} />
                ) : null}
              </AppCard>
              {data.balances.length ? (
                data.balances.map(balance => (
                  <AppCard key={balance.leaveTypeId} style={styles.card}>
                    <Text style={[styles.title, { color: palette.textPrimary }]}>
                      {formatDisplayLabel(balance.leaveTypeName)}
                    </Text>
                    <Text style={[styles.meta, { color: palette.textSecondary }]}>
                      {formatDisplayLabel(balance.leaveTypeCode)}
                      {balance.isLossOfPay ? ' · no balance required' : balance.requiresBalance ? '' : ' · not tracked'}
                    </Text>
                    <Text
                      style={[
                        styles.days,
                        {
                          color: leaveBalanceDisplayColor(
                            balance.availableBalance,
                            palette.primary,
                            palette.error,
                          ),
                        },
                      ]}
                    >
                      {balance.isLossOfPay
                        ? `Days taken (${data.leaveYear}): ${balance.daysTaken ?? 0}`
                        : `Available: ${formatLeaveBalanceValue(balance.availableBalance)}`}
                    </Text>
                    {balance.isLossOfPay ? (
                      <Text style={[styles.meta, { color: palette.textSecondary }]}>
                        Pending: {balance.pendingLeaveDays} day(s)
                      </Text>
                    ) : <>
                    <Text style={[styles.meta, { color: palette.textSecondary }]}>
                      Opening {formatLeaveBalanceValue(balance.openingBalance)}
                      {' · '}Credited {formatLeaveBalanceValue(balance.creditedDays)}
                      {' · '}Used {formatLeaveBalanceValue(balance.debitedDays)}
                    </Text>
                    <Text style={[styles.meta, { color: palette.textSecondary }]}>
                      Adjusted {formatLeaveBalanceValue(balance.adjustedDays)}
                      {' · '}Pending {formatLeaveBalanceValue(balance.pendingLeaveDays)}
                      {balance.annualEntitlement != null
                        ? ` · Entitlement ${formatLeaveBalanceValue(balance.annualEntitlement)}`
                        : ''}
                      {balance.monthlyAccrual != null
                        ? ` · Accrual ${formatLeaveBalanceValue(balance.monthlyAccrual)}/month`
                        : ''}
                    </Text>
                    </>}
                    {canUpdate && balance.requiresBalance && !balance.isLocked ? (
                      <AppButton
                        title="Update balance"
                        variant="secondary"
                        onPress={() =>
                          navigation.navigate(routes.leaveBalanceUpdate, {
                            employeeId: route.params.employeeId,
                            leaveTypeId: balance.leaveTypeId,
                            leaveYear: Number(year),
                          })
                        }
                      />
                    ) : null}
                    {balance.isLocked ? (
                      <Text style={[styles.meta, { color: palette.textSecondary }]}>
                        This balance is locked.
                      </Text>
                    ) : null}
                  </AppCard>
                ))
              ) : (
                <EmptyState
                  title="No leave types"
                  message="No leave types are configured for this organization."
                />
              )}
            </>
          ) : null}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg, gap: spacing.sm, paddingBottom: spacing.xl },
  card: { gap: spacing.xs },
  title: typography.sectionTitle,
  meta: typography.body,
  days: { ...typography.body, fontWeight: '700' },
});
